import React, { useEffect, useState, useMemo } from 'react'
import '../../css/instagramSlider.css'
const USER_ID = '25548462544751705'
const ACCESS_TOKEN = process.env.REACT_APP_IG_TOKEN // Or hardcode for now

const FIELDS = [
  'id',
  'caption',
  'media_type',
  'media_url',
  'thumbnail_url',
  'permalink',
  'timestamp'
].join(',')

const ITEMS_PER_PAGE = 4

function InstagramSlider() {
  const [posts, setPosts] = useState([])
  const [startIndex, setStartIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    async function fetchPosts() {
      try {
        if (!ACCESS_TOKEN) {
          throw new Error('Missing Instagram token. Set REACT_APP_IG_TOKEN in your environment.')
        }

        const url = new URL(`https://graph.instagram.com/${USER_ID}/media`)
        url.searchParams.set('fields', FIELDS)
        url.searchParams.set('access_token', ACCESS_TOKEN)

        const res = await fetch(url.toString())
        if (!res.ok) {
          const text = await res.text()
          throw new Error(`API error: ${res.status} ${text}`)
        }

        const json = await res.json()
        setPosts(json.data || [])
      } catch (err) {
        console.error(err)
        setError(err.message || 'Failed to load Instagram posts.')
      } finally {
        setLoading(false)
      }
    }

    fetchPosts()
  }, [])

  const visiblePosts = useMemo(() => {
    if (!posts.length) return []
    return posts.slice(startIndex, startIndex + ITEMS_PER_PAGE)
  }, [posts, startIndex])

  const canGoNext = startIndex + ITEMS_PER_PAGE < posts.length
  const canGoPrev = startIndex > 0

  const handleNext = () => {
    if (canGoNext) setStartIndex(prev => prev + ITEMS_PER_PAGE)
  }

  const handlePrev = () => {
    if (canGoPrev) setStartIndex(prev => prev - ITEMS_PER_PAGE)
  }

  if (loading) {
    return <div className='ig-status'>Loading posts…</div>
  }

  if (error) {
    return <div className='ig-status ig-status--error'>{error}</div>
  }

  if (!posts.length) {
    return <div className='ig-status'>No posts found.</div>
  }

  return (
    <section className='ig-section'>
      <div className='ig-header'>
        <div>
          <h2 className='ig-title'>Latest Instagram Posts</h2>
          <p className='ig-subtitle'>Pulled directly from your Instagram feed.</p>
        </div>

        <div className='ig-controls'>
          <button
            type='button'
            className='ig-icon-btn'
            onClick={handlePrev}
            disabled={!canGoPrev}
            aria-label='Previous posts'
          >
            ‹
          </button>
          <button
            type='button'
            className='ig-icon-btn'
            onClick={handleNext}
            disabled={!canGoNext}
            aria-label='Next posts'
          >
            ›
          </button>
        </div>
      </div>

      <div className='ig-grid'>
        {visiblePosts.map(post => (
          <InstagramCard key={post.id} post={post} />
        ))}
      </div>
    </section>
  )
}

function InstagramCard({ post }) {
  const { caption, media_type, media_url, thumbnail_url, permalink, timestamp } = post

  const isVideo = media_type === 'VIDEO'
  const isCarousel = media_type === 'CAROUSEL_ALBUM'
  const date = timestamp ? new Date(timestamp) : null

  const formattedDate = date
    ? date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    : ''

  const truncatedCaption = caption && caption.length > 130 ? caption.slice(0, 127) + '...' : caption

  return (
    <article className='ig-card'>
      <a href={permalink} target='_blank' rel='noreferrer' className='ig-media-link'>
        <MediaContent
          isVideo={isVideo}
          mediaUrl={media_url}
          thumbnailUrl={thumbnail_url}
          alt={caption || 'Instagram post'}
        />
        <div className='ig-chip-row'>
          {isVideo && <span className='ig-chip'>Video</span>}
          {isCarousel && <span className='ig-chip'>Album</span>}
        </div>
      </a>

      <div className='ig-card-body'>
        {formattedDate && <p className='ig-date'>{formattedDate}</p>}
        {truncatedCaption && (
          <p className='ig-caption' title={caption}>
            {truncatedCaption}
          </p>
        )}
        <div className='ig-footer'>
          <a href={permalink} target='_blank' rel='noreferrer' className='ig-footer-link'>
            View on Instagram
          </a>
        </div>
      </div>
    </article>
  )
}

function MediaContent({ isVideo, mediaUrl, thumbnailUrl, alt }) {
  // Use full media_url, fallback to thumbnail if needed
  const poster = thumbnailUrl || mediaUrl

  return (
    <div className='ig-media-wrapper'>
      {isVideo ? (
        <video className='ig-media' src={mediaUrl} poster={poster} controls />
      ) : (
        <img className='ig-media' src={mediaUrl} alt={alt} loading='lazy' />
      )}
    </div>
  )
}

export default InstagramSlider
